import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { TaxonomyWrapper } from './TaxonomyWrapper';
import { Typography, SearchInput } from 'app/components/ui';
import {
  usePresentationTree,
  PresentationTreeNetwork,
  TaxonomyChild,
  TagWithMetadata,
  useExtensions,
  useDocumentExtensionSchema,
  useDocumentTaxonomyConfig,
} from 'entities/tag';
import Spinner from 'shared/feedback/Spinner';
import { TaxonomyFooter } from '../Footer/TaxonomyFooter';
import { useTaxonomySearch } from './useTaxonomySearch';
import { filterVisibleNetworks } from './filterVisibleNetworks';

import './Taxonomy.scss';
import { mergeExtensionsIntoPresentationTree } from 'app/utils/tag-utils';
import { UsedConceptRoles, isConceptUsedInRole } from './usedConceptRoles';
import { resolveConceptNode } from './resolveConceptNode';
import { useEditorDocument } from 'app/api/documents/useEditorData';

interface TaxonomyProps {
  selectedTagId: string | null;
  preferredNodePath?: string | null;
  preferredLinkroleUri?: string | null;
  isContentMarked: boolean;
  usedConceptRoles: UsedConceptRoles;
  documentTags: TagWithMetadata[];
  onSelectionChange: (tag: TaxonomyChild | null, nodePath?: string) => void;
  onDetailsClick?: (tag: TaxonomyChild) => void;
  onExtensionClick?: (tagId: string) => void;
  onHiddenTagCreated?: (tag: unknown) => void;
  onCellTagsApplied?: (tagIds: string[]) => void;
  hideExtensions?: boolean;
  hideFooter?: boolean;
}

export const Taxonomy = ({
  selectedTagId,
  preferredNodePath,
  preferredLinkroleUri,
  isContentMarked,
  usedConceptRoles,
  documentTags,
  onSelectionChange,
  onDetailsClick,
  onExtensionClick,
  onHiddenTagCreated,
  onCellTagsApplied,
  hideExtensions,
  hideFooter,
}: TaxonomyProps) => {
  const { t } = useTranslation('taxonomy');

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const { data: pDocument } = useEditorDocument();
  const documentSuperId = pDocument?.superId;

  const { data: rawTaxonomyData, isLoading, isError } = usePresentationTree(documentSuperId);

  const { data: taxonomyConfig } = useDocumentTaxonomyConfig(documentSuperId);
  const activeTaxonomyId = taxonomyConfig?.taxonomySelections?.find((t) => t.isActive)?.taxonomyId ?? taxonomyConfig?.taxonomySelections?.[0]?.taxonomyId;
  const { data: extensions } = useExtensions(documentSuperId, activeTaxonomyId);
  const { data: extensionSchema } = useDocumentExtensionSchema(documentSuperId);

  const { taxonomyData, extensionConceptNames } = useMemo(
    () => mergeExtensionsIntoPresentationTree(rawTaxonomyData, hideExtensions ? [] : extensions, extensionSchema?.prefix ?? null),
    [rawTaxonomyData, extensions, extensionSchema?.prefix, hideExtensions]
  );

  const [selectedTag, setSelectedTag] = useState<TaxonomyChild | null>(null);
  const [selectedNodePath, setSelectedNodePath] = useState<string | null>(null);

  const handleSelectTag = useCallback(
    (tag: TaxonomyChild, nodePath?: string) => {
      if (selectedTag?.id === tag.id) {
        if ((nodePath ?? null) === selectedNodePath) {
          setSelectedTag(null);
          setSelectedNodePath(null);
          onSelectionChange(null);
        } else {
          setSelectedNodePath(nodePath ?? null);
          onSelectionChange(tag, nodePath);
        }
        return;
      }
      setSelectedTag(tag);
      setSelectedNodePath(nodePath ?? null);
      onSelectionChange(tag, nodePath);
    },
    [selectedTag, selectedNodePath, onSelectionChange]
  );

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const allNetworks = taxonomyData?.presentationTree?.networks;
  const linkroleOverrides = useMemo(() => {
    const overrides = taxonomyConfig?.taxonomyOverrides;
    if (!overrides || !activeTaxonomyId) return undefined;
    return overrides[activeTaxonomyId];
  }, [taxonomyConfig, activeTaxonomyId]);
  const hiddenURIs = useMemo(() => {
    if (!linkroleOverrides) return new Set<string>();
    const set = new Set<string>();
    for (const [uri, ov] of Object.entries(linkroleOverrides)) {
      if (ov?.hidden === true) set.add(uri);
    }
    return set;
  }, [linkroleOverrides]);
  const aliasByURI = useMemo(() => {
    const map = new Map<string, string>();
    if (!linkroleOverrides) return map;
    for (const [uri, ov] of Object.entries(linkroleOverrides)) {
      if (typeof ov?.alias === 'string' && ov.alias.length > 0) map.set(uri, ov.alias);
    }
    return map;
  }, [linkroleOverrides]);
  const networks = useMemo(() => (allNetworks ? filterVisibleNetworks(allNetworks, hiddenURIs) : allNetworks), [allNetworks, hiddenURIs]);

  useEffect(() => {
    const presentationNetworks = taxonomyData?.presentationTree?.networks;
    if (!selectedTagId || !presentationNetworks) {
      setSelectedTag(null);
      setSelectedNodePath(null);
      return;
    }
    const resolved = resolveConceptNode(presentationNetworks, selectedTagId, preferredLinkroleUri, hiddenURIs, preferredNodePath);
    if (selectedTag?.id === selectedTagId && selectedNodePath === (resolved?.nodePath ?? null)) return;
    setSelectedTag(resolved?.node ?? null);
    setSelectedNodePath(resolved?.nodePath ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTagId, taxonomyData, preferredNodePath, preferredLinkroleUri, hiddenURIs]);
  const { filteredNetworkIndices, searchExpandIdsByNetwork, searchVisibleIdsByNetwork, isSearching } = useTaxonomySearch(networks, debouncedSearch);

  const [expandToTagId] = useState(selectedTagId);
  const [expandToLinkroleUri] = useState(preferredLinkroleUri);

  const expandTarget = useMemo<{ network: PresentationTreeNetwork; ancestorIds: Set<string> } | null>(() => {
    if (!expandToTagId || !taxonomyData?.presentationTree) return null;
    return resolveConceptNode(taxonomyData.presentationTree.networks ?? [], expandToTagId, expandToLinkroleUri, hiddenURIs);
  }, [expandToTagId, expandToLinkroleUri, taxonomyData, hiddenURIs]);

  if (isLoading) {
    return (
      <section className="d-flex justify-content-center py-4">
        <Spinner />
      </section>
    );
  }

  if (isError) {
    return (
      <section className="px-3 py-4">
        <Typography>{t('load.error')}</Typography>
      </section>
    );
  }

  if (!taxonomyData || !taxonomyData.presentationTree) {
    return (
      <section className="px-3 py-4">
        <Typography>{t('no.imported')}</Typography>
      </section>
    );
  }

  return (
    <>
      <div className="p-3 taxonomy__search-bar">
        <SearchInput searchInput={searchInput} setSearchInput={setSearchInput} />
      </div>

      <section className="taxonomy__scroll-region" onMouseDown={(e) => e.preventDefault()}>
        <div className="px-3">
          {((networks ?? []) as PresentationTreeNetwork[])
            .map((tax, idx) => ({ tax, idx }))
            .filter(({ idx }) => !isSearching || filteredNetworkIndices?.has(idx))
            .map(({ tax, idx }) => (
              <TaxonomyWrapper
                key={idx}
                {...tax}
                selectedTagId={selectedTag?.id ?? null}
                selectedNodePath={selectedNodePath}
                expandToIds={!isSearching && expandTarget?.network === tax ? expandTarget.ancestorIds : null}
                searchExpandIds={isSearching ? searchExpandIdsByNetwork?.get(idx) ?? null : null}
                searchVisibleIds={isSearching ? searchVisibleIdsByNetwork?.get(idx) ?? null : null}
                isSearching={isSearching}
                onSelectTag={handleSelectTag}
                onDetailsClick={onDetailsClick}
                onExtensionClick={onExtensionClick}
                usedConceptRoles={usedConceptRoles}
                extensionTags={extensionConceptNames}
                aliasOverride={aliasByURI.get(tax.linkrole?.roleURI)}
              />
            ))}

          {isSearching && filteredNetworkIndices?.size === 0 && <Typography>{t('no.results')}</Typography>}
          {!isSearching && (networks ?? []).length === 0 && <Typography>{t('no.results')}</Typography>}
        </div>
      </section>

      {!hideFooter && (
        <TaxonomyFooter
          selectedTag={selectedTag}
          selectedLinkroleUri={selectedNodePath ? selectedNodePath.split('>')[0] : null}
          isContentMarked={isContentMarked}
          isTagUsed={
            selectedNodePath
              ? isConceptUsedInRole(usedConceptRoles, selectedTag?.id ?? '', selectedNodePath.split('>')[0])
              : usedConceptRoles.has(selectedTag?.id ?? '')
          }
          documentTags={documentTags}
          onExtensionClick={onExtensionClick}
          onHiddenTagCreated={onHiddenTagCreated}
          onCellTagsApplied={onCellTagsApplied}
        />
      )}
    </>
  );
};
